import type { KindHandler } from './types';

/** Keys under /crew live 7 days, as long as an item may wait; everywhere else they live 24 h (jobs B9). */
const LONG_LIVED_PREFIX = '/v1/crew';

/** A `.` or `..` segment, also percent-encoded: the browser would normalize it to another path than the one checked. */
export function hasDotSegment(path: string): boolean {
  return path.split(/[?#]/)[0].split('/').some((segment) => {
    let decoded = segment;
    try {
      decoded = decodeURIComponent(segment);
    } catch {
      return true; // a malformed escape is refused too
    }
    return decoded === '.' || decoded === '..';
  });
}

/** `path` is `prefix` itself or below it, by segment: '/v1/crew' covers '/v1/crew/x' but not '/v1/crewish'. */
export function isUnder(path: string, prefix: string): boolean {
  if (hasDotSegment(path)) return false;
  const base = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
  return path === base || path.startsWith(`${base}/`) || path.startsWith(`${base}?`);
}

export interface Registry {
  register<P>(handler: KindHandler<P>): void;
  get(kind: string): KindHandler<unknown> | undefined;
}

export function createRegistry(): Registry {
  const kinds = new Map<string, KindHandler<unknown>>();
  return {
    register(handler) {
      if (!handler.kind) throw new Error('An outbox kind needs a name');
      if (kinds.has(handler.kind)) throw new Error(`Outbox kind ${handler.kind} is already registered`);
      if (handler.paths.length === 0) throw new Error(`Outbox kind ${handler.kind} declares no paths`);
      for (const path of handler.paths) {
        if (!path.startsWith('/v1/')) throw new Error(`Outbox kind ${handler.kind}: path ${path} is not under /v1/`);
        if (hasDotSegment(path)) throw new Error(`Outbox kind ${handler.kind}: path ${path} has a . or .. segment`);
        if (!isUnder(path, LONG_LIVED_PREFIX) && !handler.naturalIdempotency) {
          throw new Error(
            `Outbox kind ${handler.kind}: ${path} is outside /crew, where keys expire before the item does; ` +
              'it must be naturally idempotent (naturalIdempotency: true)',
          );
        }
      }
      kinds.set(handler.kind, handler as KindHandler<unknown>);
    },
    get: (kind) => kinds.get(kind),
  };
}

/** The app's kinds, registered by the features that own them (F9, F13). */
export const kindRegistry = createRegistry();

export function registerKind<P>(handler: KindHandler<P>): void {
  kindRegistry.register(handler);
}
